import { PortfolioFieldKey, PortfolioType } from 'pco-product-service-internal-consumer';
import { BaseFieldDescriptor, FieldDescriptor } from '../../types/portfolios/portfolioFieldData';

export const SERVICING_ROLE_FIELDS = ['servicingRoleType', 'servicingUser'] as const;

export type ServicingRoleField = (typeof SERVICING_ROLE_FIELDS)[number];

export const EXTERNAL_ACCOUNT_NUMBER_MAX_LENGTH = 20;
export const REMARKS_MAX_LENGTH = 255;

const descriptors = {
  details: {
    apiField: PortfolioFieldKey.Details,
    requiredMessage: 'Details are required',
  },
  externalAccountNumber: {
    requiredMessage: 'External account number is required',
    formOnly: true,
    localRulesByPortfolioType: {
      [PortfolioType.Isa]: { state: 'editable', required: true },
      [PortfolioType.ExCustody]: { state: 'editable', required: true },
    },
  },
  externalAccountType: {
    requiredMessage: 'External account type is required',
    formOnly: true,
    localRulesByPortfolioType: {
      [PortfolioType.Isa]: { state: 'editable', required: true },
      [PortfolioType.ExCustody]: { state: 'editable', required: true },
    },
  },
  remarks: {
    apiField: PortfolioFieldKey.Remarks,
    requiredMessage: 'Remarks are required',
    formOnly: true,
  },
  mainCustodyBr: {
    apiField: PortfolioFieldKey.BrNumber,
    requiredMessage: 'BR is required',
    formOnly: true,
  },
  mainCustodyLinkType: {
    apiField: PortfolioFieldKey.LinkType,
    requiredMessage: 'Link type is required',
    formOnly: true,
  },
  mainCustodyPortfolioId: {
    apiField: PortfolioFieldKey.LinkedPortfolioId,
    requiredMessage: 'Portfolio ID is required',
    formOnly: true,
  },
} satisfies Record<string, FieldDescriptor>;

export const PORTFOLIO_BASE_FIELDS = {
  referenceCurrency: {
    apiField: PortfolioFieldKey.ReferenceCurrency,
    requiredMessage: 'Reference currency is required',
    fixedRule: () => ({ state: 'disabled' }),
  },
  sequenceNumber: {
    apiField: PortfolioFieldKey.SequenceNumber,
    requiredMessage: 'Sequence number is required',
    fixedRule: (isEdit) => ({ state: isEdit ? 'disabled' : 'editable', required: true }),
  },
  chargingCurrency: {
    apiField: PortfolioFieldKey.ChargingCurrency,
    requiredMessage: 'Charging currency is required',
    fixedRule: () => ({ state: 'disabled', required: true }),
  },
  rubrik: {
    apiField: PortfolioFieldKey.Rubrik,
    fixedRule: () => ({ state: 'editable' }),
  },
  specialBoEnabled: {
    apiField: PortfolioFieldKey.SpecialBoEnabled,
    fixedRule: () => ({ state: 'editable' }),
  },
  allocatedBo: {
    apiField: PortfolioFieldKey.AllocatedBo,
    requiredMessage: 'Special beneficial owner is required',
    fixedRule: () => ({ state: 'editable', required: true }),
  },
} satisfies Record<string, BaseFieldDescriptor>;

export type PortfolioBaseField = keyof typeof PORTFOLIO_BASE_FIELDS;

export type PortfolioTypeDependentField = keyof typeof descriptors;

export const PORTFOLIO_TYPE_DEPENDENT_FIELDS: Record<PortfolioTypeDependentField, FieldDescriptor> = descriptors;

export const getFormOnlyFields = (): PortfolioTypeDependentField[] =>
  (Object.keys(PORTFOLIO_TYPE_DEPENDENT_FIELDS) as PortfolioTypeDependentField[]).filter(
    (field) => PORTFOLIO_TYPE_DEPENDENT_FIELDS[field].formOnly,
  );
